import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasCreatorAccess } from "../utils/roleUtils";
import {
  extractDropId,
  getDayLabel,
  getNotificationCategory,
  markAllInboxNotificationsRead,
  markInboxNotificationRead,
  subscribeToInboxNotifications,
  timeSince,
} from "../services/notificationsInboxService";
import "./NotificationDropdown.css";

const STROKE = 1.8;

const FAN_TABS = [
  { id: "all", label: "All" },
  { id: "drops", label: "Drops" },
  { id: "credits", label: "Credits" },
];

const CREATOR_TABS = [
  { id: "all", label: "All" },
  { id: "drops", label: "Drops" },
  { id: "wallet", label: "Earnings" },
  { id: "wall", label: "Wall" },
];

function BadgeIcon({ category, type }) {
  const cat = category === "wallet" ? "payout" : category;
  const cls = {
    drops: "nib-drop",
    credits: "nib-credit",
    wall: "nib-note",
    payout: "nib-payout",
    wallet: "nib-payout",
    all: type === "follow" ? "nib-follow" : "nib-featured",
  }[cat] || "nib-featured";

  const icons = {
    drops: <polygon points="5 3 19 12 5 21 5 3" />,
    credits: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
      </>
    ),
    wall: (
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    ),
    payout: (
      <>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </>
    ),
    follow: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
    featured: (
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    ),
  };

  const key = cat === "nib-follow" ? "follow" : cat.replace("nib-", "");
  const iconKey = ["drops", "credits", "wall", "payout"].includes(key) ? key : type === "follow" ? "follow" : "featured";

  return (
    <div className={`ni-icon-badge ${cls}`}>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        {icons[iconKey]}
      </svg>
    </div>
  );
}

function FallbackIcon({ category }) {
  const styles = {
    drops: { bg: "var(--niche-light)", border: "rgba(92,139,110,0.2)", stroke: "var(--niche)" },
    credits: { bg: "var(--gold-light)", border: "rgba(196,121,10,0.2)", stroke: "var(--gold)" },
    wall: { bg: "var(--gold-light)", border: "rgba(196,121,10,0.2)", stroke: "var(--gold)" },
    wallet: { bg: "var(--green-light)", border: "rgba(26,140,82,0.2)", stroke: "var(--green)" },
    payout: { bg: "var(--green-light)", border: "rgba(26,140,82,0.2)", stroke: "var(--green)" },
  };
  const style = styles[category] || styles.credits;

  return (
    <div className="ni-icon-fallback" style={{ background: style.bg, borderColor: style.border }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
        {category === "drops" && <polygon points="5 3 19 12 5 21 5 3" />}
        {(category === "wallet" || category === "payout") && (
          <>
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
          </>
        )}
        {category === "credits" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
          </>
        )}
        {category === "wall" && (
          <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
        )}
        {!["drops", "wallet", "payout", "credits", "wall"].includes(category) && (
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        )}
      </svg>
    </div>
  );
}

function NotificationText({ item }) {
  const name = item.creatorDisplayName || item.senderDisplayName;
  const body = item.text || item.message || item.body || "";

  if (name && body) {
    return (
      <div className="ni-text">
        <strong>{name}</strong> {body}
      </div>
    );
  }

  const quoteMatch = body.match(/"([^"]+)"/);
  if (quoteMatch) {
    const [full, quoted] = quoteMatch;
    const [before, after] = body.split(full);
    return (
      <div className="ni-text">
        {before}<strong>{quoted}</strong>{after}
      </div>
    );
  }

  const payoutMatch = body.match(/\$[\d,.]+/);
  if (payoutMatch) {
    const [before, after] = body.split(payoutMatch[0]);
    return (
      <div className="ni-text">
        {before}<strong>{payoutMatch[0]}</strong>{after}
      </div>
    );
  }

  return <div className="ni-text">{body || item.title || "New activity"}</div>;
}

function groupByDay(items) {
  const groups = [];
  let currentLabel = null;
  items.forEach((item) => {
    const label = getDayLabel(item.createdAt);
    if (label !== currentLabel) {
      currentLabel = label;
      groups.push({ type: "label", label });
    }
    groups.push({ type: "item", item });
  });
  return groups;
}

export default function NotificationDropdown({ isOpen, onClose, onUnreadChange }) {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const isCreator = hasCreatorAccess(userProfile);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const tabs = isCreator ? CREATOR_TABS : FAN_TABS;

  useEffect(() => {
    if (!currentUser?.uid) {
      setItems([]);
      return undefined;
    }

    setLoading(true);
    const unsub = subscribeToInboxNotifications(
      currentUser.uid,
      (next) => {
        setItems(next);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [currentUser?.uid]);

  const categorized = useMemo(
    () => items.map((item) => ({
      ...item,
      category: getNotificationCategory(item.type, isCreator),
    })),
    [items, isCreator]
  );

  const filtered = useMemo(() => {
    if (activeTab === "all") return categorized;
    return categorized.filter((item) => item.category === activeTab);
  }, [categorized, activeTab]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  useEffect(() => {
    onUnreadChange?.(unreadCount);
  }, [unreadCount, onUnreadChange]);

  const handleItemClick = async (item) => {
    if (!item.read && currentUser?.uid) {
      await markInboxNotificationRead(currentUser.uid, item).catch(console.error);
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
    }

    onClose();

    const dropId = extractDropId(item);
    if (dropId) {
      navigate(`/drop/${dropId}`);
      return;
    }

    if (item.link) {
      navigate(item.link);
      return;
    }

    if (item.category === "wall") {
      navigate(isCreator ? "/appreciation-wall" : "/notifications");
      return;
    }

    if (item.category === "wallet") {
      navigate("/earnings");
      return;
    }

    navigate("/notifications");
  };

  const handleMarkAllRead = async () => {
    if (!currentUser?.uid) return;
    await markAllInboxNotificationsRead(currentUser.uid, items).catch(console.error);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  if (!isOpen) return null;

  const grouped = groupByDay(filtered);

  return (
    <div className={`notif-panel open`} role="dialog" aria-label="Notifications">
      <div className="np-header">
        <span className="np-title">Notifications</span>
        <button type="button" className="np-mark-read" onClick={handleMarkAllRead}>
          Mark all read
        </button>
      </div>

      <div className="np-tabs">
        {tabs.map((tab) => {
          const tabUnread = tab.id === "all"
            ? unreadCount
            : categorized.filter((n) => !n.read && n.category === tab.id).length;

          return (
            <button
              key={tab.id}
              type="button"
              className={`np-tab${activeTab === tab.id ? " active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.id === "all" && tabUnread > 0 && (
                <span className="np-tab-badge">{tabUnread}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="np-list">
        {loading ? (
          <div className="np-loading">Loading notifications…</div>
        ) : filtered.length === 0 ? (
          <div className="np-empty">
            <div className="np-empty-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </div>
            <div className="np-empty-text">Nothing yet</div>
            <div className="np-empty-sub">
              When something happens — a new drop, a pinned note, a payout — it&apos;ll show up here.
            </div>
          </div>
        ) : (
          grouped.map((entry, idx) => {
            if (entry.type === "label") {
              return <div key={`label-${entry.label}-${idx}`} className="np-day-label">{entry.label}</div>;
            }

            const item = entry.item;
            const avatar = item.creatorAvatar || item.senderAvatar || item.avatar;
            const showAvatar = Boolean(
              avatar && (
                ["drops", "wall"].includes(item.category)
                || item.type === "follow"
                || item.type === "broadcast"
                || item.type === "drop"
              )
            );

            return (
              <button
                key={item.id}
                type="button"
                className={`notif-item${!item.read ? " unread" : ""}`}
                onClick={() => handleItemClick(item)}
              >
                <div className="ni-icon-wrap">
                  {showAvatar ? (
                    <>
                      <img src={avatar} alt="" />
                      <BadgeIcon category={item.category} type={item.type} />
                    </>
                  ) : (
                    <>
                      <FallbackIcon category={item.category} />
                      {item.category !== "drops" && item.category !== "wallet" && (
                        <BadgeIcon category={item.category} type={item.type} />
                      )}
                    </>
                  )}
                </div>
                <div className="ni-content">
                  <NotificationText item={item} />
                  <div className="ni-time">{timeSince(item.createdAt)}</div>
                </div>
                {!item.read && <div className="ni-unread-dot" />}
              </button>
            );
          })
        )}
      </div>

      <div className="np-footer">
        <Link to="/notifications" onClick={onClose}>View all notifications →</Link>
      </div>
    </div>
  );
}

export function useNotificationUnreadCount(userId) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId) {
      setCount(0);
      return undefined;
    }
    return subscribeToInboxNotifications(userId, (items) => {
      setCount(items.filter((n) => !n.read).length);
    });
  }, [userId]);

  return count;
}
